/**
 * A patient's documents, as every desk sees them (QA report §5.2).
 *
 *   AttachmentCard    one document: its picture, or a lit badge for its type
 *   AttachmentGrid    the cards, and the viewer they open
 *   AttachmentViewer  pictures, PDFs, video, audio and text in the page;
 *                     anything else is offered as a download only
 *   AttachmentStrip   a row of thumbnails for a department's request card
 *
 * The thumbnails come with the list (the uploader's browser made them), so a
 * grid paints pictures without anybody clicking. A picture filed before
 * thumbnails existed loads its own bytes instead, when it is small enough.
 *
 * Nothing here renders a type outside domain/attachments.js's inline list:
 * the bytes are shown from a blob: URL this page made, which runs with this
 * page's origin, so an uploaded HTML page or SVG is only ever downloaded.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, EmptyState, ErrorState, Icon, Modal, Spinner } from '../../../components/common';
import { KIND_META, formatBytes, isInlineSafe, kindOf, orderForRequest } from '../../../domain/attachments';
import { formatDateTime } from '../labels';
import { attachmentBlob, patientAttachments } from './upload';
import { OcrBadge, OcrTextPanel } from './OcrText';

/** Pictures filed before thumbnails existed load themselves up to this size. */
const LAZY_PICTURE_BYTES = 8 * 1024 * 1024;

const titleOf = (row) => row.title || row.fileName || 'مرفق';

/**
 * The attachment's bytes as an object URL, revoked when the caller unmounts
 * or moves on. `row` null means "nothing to load".
 */
export function useAttachmentUrl(row) {
  const id = row ? row.id : null;
  const sizeBytes = row ? row.sizeBytes : null;
  const [state, setState] = useState({ url: null, blob: null, error: null, loading: false });

  useEffect(() => {
    if (!id) {
      setState({ url: null, blob: null, error: null, loading: false });
      return undefined;
    }
    let alive = true;
    let url = null;
    setState({ url: null, blob: null, error: null, loading: true });
    attachmentBlob({ id, sizeBytes })
      .then((blob) => {
        if (!alive) return;
        url = URL.createObjectURL(blob);
        setState({ url, blob, error: null, loading: false });
      })
      .catch((error) => alive && setState({ url: null, blob: null, error, loading: false }));
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [id, sizeBytes]);

  return state;
}

/** Save the attachment to the user's disk under its own name. */
export async function downloadAttachment(row, blob = null) {
  const bytes = blob || (await attachmentBlob(row));
  const url = URL.createObjectURL(bytes);
  const a = document.createElement('a');
  a.href = url;
  a.download = row.fileName || titleOf(row);
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

/** The extension, as a badge reads it: "PDF", "DOCX". */
function extensionOf(row) {
  const name = String(row.fileName || '');
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toUpperCase().slice(0, 5) : '';
}

export function AttachmentCard({ row, onOpen, onRemove, linked = false, delay = 0 }) {
  const kind = kindOf(row.mimeType, row.fileName);
  const meta = KIND_META[kind];
  const lazy = !row.thumbnail && kind === 'image' && isInlineSafe(row.mimeType) && (row.sizeBytes || 0) <= LAZY_PICTURE_BYTES;
  const { url: lazyUrl } = useAttachmentUrl(lazy ? row : null);
  const picture = row.thumbnail || lazyUrl;

  return (
    <div
      className={`attachment-card tone-${meta.tone} ${linked ? 'attachment-card--linked' : ''}`}
      style={{ animationDelay: `${Math.min(delay, 12) * 40}ms` }}
    >
      <button type="button" className="attachment-card__preview" onClick={() => onOpen(row)} aria-label={`عرض ${titleOf(row)}`}>
        {picture ? (
          <img src={picture} alt="" loading="lazy" className="attachment-card__img" />
        ) : (
          <span className="attachment-card__badge">
            <span className="tile3d__face">
              <Icon name={meta.icon} size={26} className="tile3d__icon" />
            </span>
            <span className="attachment-card__ext">{extensionOf(row) || meta.label}</span>
          </span>
        )}
        {kind === 'video' && (
          <span className="attachment-card__play" aria-hidden="true">
            <Icon name="play" size={18} />
          </span>
        )}
        {linked && <span className="attachment-card__flag">لهذا الطلب</span>}
        {row.ocrStatus && row.ocrStatus !== 'skipped' && (
          <span className="attachment-card__ocr">
            <OcrBadge row={row} compact={row.ocrStatus !== 'done'} />
          </span>
        )}
      </button>
      <div className="min-w-0 px-3 pb-3 pt-2">
        <p className="truncate text-sm font-bold text-white" title={titleOf(row)}>
          {titleOf(row)}
        </p>
        <p className="truncate text-[11px] text-slate-400">
          {row.category} · {formatBytes(row.sizeBytes)}
        </p>
        <p className="truncate text-[10px] text-slate-500">
          {formatDateTime(row.createdAt)}
          {row.uploadedByName ? ` · ${row.uploadedByName}` : ''}
        </p>
      </div>
      {onRemove && (
        <button type="button" className="attachment-card__remove" onClick={() => onRemove(row)} aria-label={`حذف ${titleOf(row)}`}>
          <Icon name="trash-2" size={14} />
        </button>
      )}
    </div>
  );
}

export function AttachmentViewer({ rows, index, onClose, onIndex, onRowChange = null }) {
  const row = index === null || index === undefined ? null : rows[index] || null;
  // The text read out of the picture, shown over it (Backend/lib/ocr.js).
  const [showText, setShowText] = useState(false);
  const readable = Boolean(row && row.ocrStatus && row.ocrStatus !== 'skipped');
  const kind = row ? kindOf(row.mimeType, row.fileName) : null;
  const viewable = Boolean(row) && isInlineSafe(row.mimeType);
  const { url, blob, error, loading } = useAttachmentUrl(viewable ? row : null);
  const [zoom, setZoom] = useState(false);
  const [text, setText] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => setZoom(false), [index]);
  // A picture whose text has been read opens with the text over it.
  useEffect(() => setShowText(Boolean(row && row.ocrStatus === 'done')), [index, row?.ocrStatus]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    let alive = true;
    if (kind === 'text' && blob) blob.text().then((t) => alive && setText(t.slice(0, 200000)));
    else setText(null);
    return () => {
      alive = false;
    };
  }, [blob, kind]);

  const count = rows.length;
  const go = useCallback((step) => onIndex((i) => (count ? (i + step + count) % count : i)), [count, onIndex]);

  useEffect(() => {
    if (!row || count < 2) return undefined;
    // Right-to-left: the arrow pointing left is "next".
    const onKey = (event) => {
      if (event.key === 'ArrowLeft') go(1);
      if (event.key === 'ArrowRight') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [row, count, go]);

  const save = async () => {
    setSaving(true);
    try {
      await downloadAttachment(row, blob);
    } finally {
      setSaving(false);
    }
  };

  if (!row) return null;
  const meta = KIND_META[kind];

  return (
    <Modal
      open
      onClose={onClose}
      title={titleOf(row)}
      icon={meta.icon}
      size="xl"
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-slate-400">
            {row.category} · {formatBytes(row.sizeBytes)} · {formatDateTime(row.createdAt)}
            {row.uploadedByName ? ` · رفعه ${row.uploadedByName}` : ''}
          </p>
          <div className="flex flex-wrap gap-2">
            {count > 1 && (
              <>
                <Button variant="ghost" icon="chevron-right" onClick={() => go(-1)}>
                  السابق
                </Button>
                <span className="self-center text-xs text-slate-400" dir="ltr">
                  {index + 1} / {count}
                </span>
                <Button variant="ghost" icon="chevron-left" onClick={() => go(1)}>
                  التالي
                </Button>
              </>
            )}
            {readable && (
              <Button variant={showText ? 'success' : 'subtle'} icon="scan-text" onClick={() => setShowText((v) => !v)}>
                {showText ? 'إخفاء النص' : 'النص المستخرج'}
              </Button>
            )}
            {kind === 'image' && url && (
              <Button variant="subtle" icon={zoom ? 'zoom-out' : 'zoom-in'} onClick={() => setZoom((z) => !z)}>
                {zoom ? 'ملاءمة' : 'تكبير'}
              </Button>
            )}
            <Button icon="download" loading={saving} onClick={save}>
              تنزيل
            </Button>
          </div>
        </div>
      }
    >
      <div className="attachment-viewer">
        {!viewable ? (
          <EmptyState
            icon={meta.icon}
            title="هذا النوع يُفتح ببرنامجه"
            message="لحماية النظام لا تُعرض هذه الملفات داخل المتصفح. نزّل الملف وافتحه بالبرنامج المناسب."
            action={
              <Button icon="download" loading={saving} onClick={save}>
                تنزيل {extensionOf(row) || 'الملف'}
              </Button>
            }
          />
        ) : loading ? (
          <Spinner label="جارٍ تحميل الملف" variant="inline" />
        ) : error ? (
          <ErrorState error={error} title="تعذر فتح الملف" />
        ) : kind === 'image' ? (
          <div className={`attachment-viewer__stage relative ${zoom ? 'overflow-auto' : ''}`}>
            <img
              src={url}
              alt={titleOf(row)}
              onClick={() => setZoom((z) => !z)}
              className={zoom ? 'max-w-none cursor-zoom-out' : 'max-h-[68vh] max-w-full cursor-zoom-in object-contain'}
            />
            {readable && showText && (
              <OcrTextPanel row={row} floating onClose={() => setShowText(false)} onReread={onRowChange || undefined} />
            )}
          </div>
        ) : kind === 'pdf' ? (
          <iframe src={url} title={titleOf(row)} className="h-[70vh] w-full rounded-xl bg-white" />
        ) : kind === 'video' ? (
          <video src={url} controls className="max-h-[70vh] w-full rounded-xl bg-black" />
        ) : kind === 'audio' ? (
          <audio src={url} controls className="w-full" />
        ) : (
          <pre dir="auto" className="max-h-[70vh] overflow-auto whitespace-pre-wrap rounded-xl bg-black/30 p-4 text-sm text-slate-200">
            {text ?? ''}
          </pre>
        )}
      </div>
    </Modal>
  );
}

export function AttachmentGrid({ rows, onRemove, linkedRequestId = null, emptyMessage, onRowChange = null }) {
  const [index, setIndex] = useState(null);
  const ordered = useMemo(() => orderForRequest(rows, linkedRequestId), [rows, linkedRequestId]);

  if (!ordered.length) {
    return <EmptyState icon="paperclip" title="لا توجد مرفقات" message={emptyMessage || 'الروشتات وصور التحاليل والأشعة تظهر هنا فور رفعها.'} />;
  }
  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {ordered.map((row, i) => (
          <AttachmentCard
            key={row.id}
            row={row}
            delay={i}
            linked={Boolean(linkedRequestId) && Number(row.serviceRequestId) === Number(linkedRequestId)}
            onOpen={() => setIndex(i)}
            onRemove={onRemove}
          />
        ))}
      </div>
      <AttachmentViewer rows={ordered} index={index} onIndex={setIndex} onClose={() => setIndex(null)} onRowChange={onRowChange} />
    </>
  );
}

/**
 * The patient's documents on a department's request card: the request's own
 * first, then the rest — so the lab sees the prescription the desk uploaded
 * without opening anything (QA report §5.2). Renders nothing when there are none.
 */
export function AttachmentStrip({ patientId, serviceRequestId = null, max = 6 }) {
  const [rows, setRows] = useState(null);
  const [index, setIndex] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!patientId) return undefined;
    patientAttachments(patientId)
      .then((list) => alive && setRows(orderForRequest(list, serviceRequestId)))
      .catch(() => alive && setRows([]));
    return () => {
      alive = false;
    };
  }, [patientId, serviceRequestId]);

  if (!rows || rows.length === 0) return null;
  const shown = rows.slice(0, max);

  return (
    <div className="mt-3">
      <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold text-slate-400">
        <Icon name="paperclip" size={12} /> مرفقات المريض ({rows.length})
      </p>
      <div className="flex flex-wrap gap-2">
        {shown.map((row, i) => {
          const kind = kindOf(row.mimeType, row.fileName);
          const meta = KIND_META[kind];
          const mine = serviceRequestId && Number(row.serviceRequestId) === Number(serviceRequestId);
          return (
            <button
              key={row.id}
              type="button"
              onClick={() => setIndex(i)}
              title={`${titleOf(row)} — ${row.category}`}
              className={`attachment-chip tone-${meta.tone} ${mine ? 'attachment-chip--linked' : ''}`}
            >
              {row.thumbnail ? <img src={row.thumbnail} alt="" loading="lazy" /> : <Icon name={meta.icon} size={18} />}
            </button>
          );
        })}
        {rows.length > shown.length && (
          <button type="button" onClick={() => setIndex(shown.length)} className="attachment-chip attachment-chip--more">
            +{rows.length - shown.length}
          </button>
        )}
      </div>
      <AttachmentViewer rows={rows} index={index} onIndex={setIndex} onClose={() => setIndex(null)} />
    </div>
  );
}
